import React, { useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import { __ } from '../../../../i18n/l10n';
import theme from '../../../../styled-components/dark';
import { MissionCheck, MissionUserAvatar } from '../../assets/svg';
import { type MissionEnrollmentButtonStatus, MissionEnrollmentButtonStatuses } from '../../types/components';

const LabelPootleKeyByStatus = {
  // __('missions:Opt in')
  [MissionEnrollmentButtonStatuses.DEFAULT]: 'missions:Opt in',
  // __('missions:Log in')
  [MissionEnrollmentButtonStatuses.NOT_CONNECTED]: 'missions:Log in',
  // __('missions:AlreadyOpted')
  [MissionEnrollmentButtonStatuses.PASSIVE]: '',
};

const BORDER_WIDTH = 0.12; // em
const TRANSITION_DURATION = 2000; // ms

type WrapperType = {
  $status: MissionEnrollmentButtonStatus;
  $shouldTransition: boolean;
};

const Wrapper = styled.div<WrapperType>`
  position: relative;
  height: 2.4em;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 50em;
  padding: ${BORDER_WIDTH}em;

  transition:
    background-position ${TRANSITION_DURATION}ms ease-in-out,
    transform 150ms ease-in-out,
    filter 200ms ease-in-out;
  filter: brightness(100%);
  background-size: 300%;
  transform: translateZ(0);

  ${({ $status }) => css`
    background-image: ${theme.MissionEnrollmentButton.borderGradient};

    ${$status === MissionEnrollmentButtonStatuses.PASSIVE
      ? css`
          pointer-events: none;
          background-position-x: 100%;

          &:before {
            background-position-x: 100%;
          }
        `
      : css`
          cursor: pointer;
          background-position-x: 0%;

          ${Target.__MOBILE__
            ? css`
                &:active {
                  transform: scale(0.98);
                }
              `
            : css`
                &:hover {
                  filter: brightness(110%);
                }

                &:active {
                  transform: scale(0.98);
                }
              `}

          &:before {
            background-position-x: 0%;
          }
        `}
  `}

  ${({ $shouldTransition }) =>
    !$shouldTransition
      ? css`
          transition:
            transform 150ms ease-in-out,
            filter 200ms ease-in-out;

          &:before {
            transition: none;
          }
        `
      : undefined}
`;

type ContentType = {
  $status: MissionEnrollmentButtonStatus;
  $shouldTransition: boolean;
};

const Content = styled.div<ContentType>`
  position: relative;

  padding: 2px;
  height: 100%;
  width: 100%;
  border-radius: 50em;
  background-size: 300%;
  overflow: hidden;

  transition: background-position ${TRANSITION_DURATION}ms ease-in-out;

  ${({ $status }) => css`
    background-image: ${theme.MissionEnrollmentButton.backgroundGradient};
    background-position-x: ${$status === MissionEnrollmentButtonStatuses.PASSIVE ? '100%' : '0%'};
  `}

  ${({ $shouldTransition }) =>
    !$shouldTransition
      ? css`
          transition: none;
        `
      : undefined}
`;

const IconsKeyFrames = keyframes`
    0% {
    opacity: 0;
  }

  10% {
    opacity: 1;
  }
`;

type ContentBackgroundType = {
  $isWon: boolean;
};

const ContentBackground = styled.div<ContentBackgroundType>`
  padding: 0.2em 1em;
  width: 100%;
  height: 100%;
  border-radius: 50em;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: ${({ $isWon }) => ($isWon ? theme.MissionProgressBar.progressBarBgColor : 'transparent')};
`;

type IconsWrapper = {
  $shouldTransition: boolean;
  $isWon: boolean;
};

const IconsWrapper = styled.div<IconsWrapper>`
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 0.3em;

  animation-name: ${IconsKeyFrames};
  animation-duration: ${TRANSITION_DURATION}ms;
  animation-timing-function: ease-out;

  ${({ $shouldTransition }) =>
    !$shouldTransition
      ? css`
          animation-duration: 0ms;
        `
      : undefined}

  --MissionEnrollmentButton-iconColor: ${({ $isWon }) => ($isWon ? 'black' : '#70dba6')};
`;

const UserAvatarIcon = styled(MissionUserAvatar)`
  fill: var(--MissionEnrollmentButton-iconColor, #70dba6);
`;

const CheckIcon = styled(MissionCheck)`
  stroke: var(--MissionEnrollmentButton-iconColor, #70dba6);
`;

export const MissionEnrollmentButtonLabelClassName = 'mission-enrollment-button-label';
const Label = styled.div`
  position: relative;
  padding-bottom: 0.1em;

  max-width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 500;

  &:first-letter {
    text-transform: capitalize;
  }

  color: ${theme.MissionEnrollmentButton.color};
`;

type MissionEnrollmentButtonProps = {
  className?: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  status: MissionEnrollmentButtonStatus;
  isWon?: boolean;
  shouldTransition?: boolean;
};

const MissionEnrollmentButton = ({
  className = '',
  onClick,
  status,
  isWon = false,
  shouldTransition = true,
}: MissionEnrollmentButtonProps) => {
  const labelPootleKey = useMemo(() => LabelPootleKeyByStatus[status], [status]);
  return (
    <Wrapper className={className} $status={status} $shouldTransition={shouldTransition} onClick={onClick}>
      <Content $status={status} $shouldTransition={shouldTransition}>
        <ContentBackground $isWon={isWon}>
          <CSSTransition
            classNames="mission-enrollment-button-transitions"
            timeout={shouldTransition ? TRANSITION_DURATION : 1}
            in={status === MissionEnrollmentButtonStatuses.PASSIVE}
            unmountOnExit
          >
            <IconsWrapper $shouldTransition={shouldTransition} $isWon={isWon}>
              <UserAvatarIcon />
              <CheckIcon />
            </IconsWrapper>
          </CSSTransition>
          <Label className={MissionEnrollmentButtonLabelClassName}>{__(labelPootleKey)}</Label>
        </ContentBackground>
      </Content>
    </Wrapper>
  );
};

export default MissionEnrollmentButton;
